import React, { useState, useEffect } from "react"
import { twMerge } from "tailwind-merge"

import FacebookIcon from "../../../components/icons/FacebookIcon"
import InstagramIcon from "../../../components/icons/InstagramIcon"
import LinkedinIcon from "../../../components/icons/LinkedinIcon"
import TikTokIcon from "../../../components/icons/TikTokIcon"
import XTwitterIcon from "../../../components/icons/XTwitterIcon"
import YoutubeIcon from "../../../components/icons/YoutubeIcon"
import { formatPhoneNumber } from "../../../utils/phoneUtils"
import { urqlClient, validEmail } from "../../../utils/utils"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import Map from "../../ProProfileShared/Map"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme, getThemeShapeClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const ContactSection = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Hook para manejar el tamaño de la pantalla
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      // Mobile: <768px (uno debajo del otro), Tablet: 768-1023px (uno debajo del otro), Desktop: 1024px+ (lado a lado)
      const desktop = width >= 1024
      setIsDesktop(desktop)
    }

    // Verificar tamaño inicial
    checkScreenSize()

    // Agregar listener para cambios de tamaño
    window.addEventListener("resize", checkScreenSize)

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize)
  }, [])
  const showContactForm = settings.showContactForm !== false
  const showPhoneNumber = settings.showPhoneNumber !== false
  const showEmail = settings.showEmail !== false
  const showMap = settings.showMap || false

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled
    if (settings.showBackgroundColor && settings.backgroundColor === "theme") {
      // Use alert yellow if bgLight is missing to surface misconfiguration
      return themeColors.bgLight || "#FFF200"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  const backgroundColor = resolveSectionBackgroundColor()
  const formButtonText = settings.formButtonText || "Send Message"
  const phoneNumber = settings.phoneNumber || practice?.phone || ""
  const email = settings.email || practice?.email || ""

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error' | null
  const [validationErrors, setValidationErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.name.trim()) {
      errors.name = "Name is required"
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required"
    } else if (!validEmail(formData.email)) {
      errors.email = "Please enter a valid email address"
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required"
    }

    return errors
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    // Clear any previous errors
    setValidationErrors({})
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const result = await urqlClient
        .mutation(
          `
          mutation SendMessage($practitionerId: ID!, $message: String!, $senderName: String!, $senderEmail: String!, $senderPhone: String!) {
            sendMessage(
              practitionerId: $practitionerId
              message: $message
              senderName: $senderName
              senderEmail: $senderEmail
              senderPhone: $senderPhone
            ) {
              result
              errors
            }
          }
        `,
          {
            practitionerId: practice.user.id,
            message: formData.message,
            senderName: formData.name,
            senderEmail: formData.email,
            senderPhone: "" // Optional field
          }
        )
        .toPromise()

      // Check for GraphQL errors first
      if (result.error) {
        setSubmitStatus("error")
        console.error("GraphQL error sending message:", result.error) // eslint-disable-line no-console
        return
      }

      if (result.data?.sendMessage?.result === "success") {
        setSubmitStatus("success")
        // Clear form
        setFormData({
          name: "",
          email: "",
          message: ""
        })
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000)
      } else {
        setSubmitStatus("error")
        const errors = result.data?.sendMessage?.errors || ["Unknown error occurred"]
        console.error("Error sending message:", errors) // eslint-disable-line no-console
      }
    } catch (error) {
      setSubmitStatus("error")
      console.error("Error submitting contact form:", error) // eslint-disable-line no-console
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderContactInfo = () => {
    const hasEmail = showEmail && !!email
    const hasPhone = showPhoneNumber && !!phoneNumber

    if (!hasEmail && !hasPhone) return null

    return (
      <div className="space-y-2 text-center">
        {hasEmail && (
          <p
            className="text-lg"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: theme?.colors?.textDark || "#000000"
            }}>
            {email}
          </p>
        )}
        {hasPhone && (
          <p
            className="text-lg"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: theme?.colors?.textDark || "#000000"
            }}>
            {formatPhoneNumber(phoneNumber)}
          </p>
        )}
      </div>
    )
  }

  const renderContactForm = () => {
    if (!showContactForm) return null

    return (
      <div
        className={twMerge("border bg-white p-8", getThemeShapeClasses(theme))}
        style={{ borderColor: theme?.colors?.stroke || "#e6e6e6" }}>
        {/* Success Message */}
        {submitStatus === "success" && (
          <div
            className={twMerge("mb-4 border-l-4 p-4", getThemeShapeClasses(theme))}
            style={{
              borderColor: "#10b981",
              backgroundColor: "#d1fae5"
            }}>
            <p
              className="font-medium"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: "#065f46"
              }}>
              ✓ Message sent successfully! We&apos;ll get back to you soon.
            </p>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === "error" && (
          <div
            className={twMerge("mb-4 border-l-4 p-4", getThemeShapeClasses(theme))}
            style={{
              borderColor: "#ef4444",
              backgroundColor: "#fee2e2"
            }}>
            <p
              className="font-medium"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: "#991b1b"
              }}>
              ✗ There was an error sending your message. Please try again.
            </p>
          </div>
        )}

        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div
            className="gap-4"
            style={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              gap: "1rem"
            }}>
            <div
              className="flex flex-col gap-1"
              style={{
                flex: isDesktop ? "1" : "none",
                width: isDesktop ? "auto" : "100%"
              }}>
              <label
                className="text-xs font-semibold"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  color: theme?.colors?.textDark || "#000000"
                }}>
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={twMerge("border px-3 py-2 focus:outline-none focus:ring-2", getThemeShapeClasses(theme))}
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  borderColor: validationErrors.name ? "#ef4444" : theme?.colors?.strokeInput || "#dedede"
                }}
              />
              {validationErrors.name && (
                <span
                  className="text-xs"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "sans-serif",
                    color: "#ef4444"
                  }}>
                  {validationErrors.name}
                </span>
              )}
            </div>
            <div
              className="flex flex-col gap-1"
              style={{
                flex: isDesktop ? "1" : "none",
                width: isDesktop ? "auto" : "100%"
              }}>
              <label
                className="text-xs font-semibold"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  color: theme?.colors?.textDark || "#000000"
                }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={twMerge("border px-3 py-2 focus:outline-none focus:ring-2", getThemeShapeClasses(theme))}
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  borderColor: validationErrors.email ? "#ef4444" : theme?.colors?.strokeInput || "#dedede"
                }}
              />
              {validationErrors.email && (
                <span
                  className="text-xs"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "sans-serif",
                    color: "#ef4444"
                  }}>
                  {validationErrors.email}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label
              className="text-xs font-semibold"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: theme?.colors?.textDark || "#000000"
              }}>
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={3}
              className={twMerge(
                "w-full border px-3 py-2 focus:outline-none focus:ring-2",
                getThemeShapeClasses(theme)
              )}
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                borderColor: validationErrors.message ? "#ef4444" : theme?.colors?.strokeInput || "#dedede"
              }}
            />
            {validationErrors.message && (
              <span
                className="text-xs"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  color: "#ef4444"
                }}>
                {validationErrors.message}
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 font-normal text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            style={applyButtonTheme(
              {
                backgroundColor: theme?.colors?.primary || "#bd654f",
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: theme?.colors?.bgLight || "#f4ece9"
              },
              theme
            )}>
            {isSubmitting ? "Sending..." : formButtonText}
          </button>
        </form>
      </div>
    )
  }

  const renderSocialLinks = () => {
    // Check multiple sources for social links (in order of priority):
    // 1. Theme footer social links (v2 builder format - where they're stored in DB)
    // 2. Practice socialLinks object (if available)
    // 3. Practice direct properties (legacy format)
    const instagram = theme?.footer?.socialLinks?.instagram || practice?.socialLinks?.instagram || practice?.instagram
    const tiktok = theme?.footer?.socialLinks?.tiktok || practice?.socialLinks?.tiktok || practice?.tiktok
    const twitter =
      theme?.footer?.socialLinks?.x ||
      theme?.footer?.socialLinks?.twitter ||
      practice?.socialLinks?.twitter ||
      practice?.socialLinks?.x ||
      practice?.twitter
    const facebook = theme?.footer?.socialLinks?.facebook || practice?.socialLinks?.facebook || practice?.facebook
    const youtube = theme?.footer?.socialLinks?.youtube || practice?.socialLinks?.youtube || practice?.youtube
    const linkedin = theme?.footer?.socialLinks?.linkedin || practice?.socialLinks?.linkedin || practice?.linkedin

    // Check if we have any social links
    const hasSocialLinks = instagram || tiktok || twitter || facebook || youtube || linkedin

    if (!hasSocialLinks) return null

    const iconColor = theme?.colors?.primary || "#bd654f"

    return (
      <div className="flex items-center justify-center gap-8">
        {instagram && (
          <a href={instagram} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">
            <InstagramIcon height={24} width={24} color={iconColor} />
          </a>
        )}
        {tiktok && (
          <a href={tiktok} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">
            <TikTokIcon height={24} width={24} color={iconColor} />
          </a>
        )}
        {twitter && (
          <a href={twitter} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">
            <XTwitterIcon height={24} width={24} color={iconColor} />
          </a>
        )}
        {facebook && (
          <a href={facebook} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">
            <FacebookIcon height={24} width={24} color={iconColor} />
          </a>
        )}
        {youtube && (
          <a href={youtube} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">
            <YoutubeIcon height={24} width={24} color={iconColor} />
          </a>
        )}
        {linkedin && (
          <a href={linkedin} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">
            <LinkedinIcon height={24} width={24} color={iconColor} />
          </a>
        )}
      </div>
    )
  }

  const contactInfo = renderContactInfo()
  const socialLinks = renderSocialLinks()

  return (
    <section
      id="contact"
      className={twMerge("px-4 py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{ backgroundColor }}>
      <AnimatedSection className="mx-auto max-w-3xl">
        {/* Header Section */}
        <div className="mb-12 space-y-4 text-center">
          {section.headline && (
            <h2
              className="text-[40px] font-bold"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#000000"
              }}>
              {section.headline}
            </h2>
          )}
          {section.subheadline && (
            <p
              className="mx-auto max-w-3xl text-xl"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: theme?.colors?.textDark || "#000000"
              }}>
              {section.subheadline}
            </p>
          )}

          {/* Section Body Content (description) - moved above form */}
          {section.body && (
            <>
              <style>{`
                .contact-section-content {
                  text-align: center !important;
                }
                .contact-section-content ul {
                  list-style-type: disc !important;
                  margin-left: 1.5rem !important;
                  margin-bottom: 1rem !important;
                }
                .contact-section-content li {
                  margin-bottom: 0.5rem !important;
                  display: list-item !important;
                }
                .contact-section-content ol {
                  list-style-type: decimal !important;
                  margin-left: 1.5rem !important;
                  margin-bottom: 1rem !important;
                }
                .contact-section-content .ql-align-right {
                  text-align: right !important;
                }
                .contact-section-content .ql-align-center {
                  text-align: center !important;
                }
                .contact-section-content .ql-align-justify {
                  text-align: justify !important;
                }
                .contact-section-content .ql-align-left {
                  text-align: left !important;
                }
                .contact-section-content a {
                  color: ${theme?.colors?.primary || "#BD654F"} !important;
                  text-decoration: underline !important;
                }
                .contact-section-content a:hover {
                  color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                  text-decoration: underline !important;
                }
                .contact-section-content p {
                  margin-bottom: 1rem !important;
                  margin-top: 0 !important;
                  background: none !important;
                  padding: 0 !important;
                }
                .contact-section-content p:first-child {
                  margin-top: 0 !important;
                }
                .contact-section-content p:last-child {
                  margin-bottom: 0 !important;
                }
                .contact-section-content strong,
                .contact-section-content b {
                  font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
                }
                .contact-section-content em,
                .contact-section-content i {
                  font-style: italic !important;
                }
                .contact-section-content u {
                  text-decoration: underline !important;
                }
                .contact-section-content h1 {
                  font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                  font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                  font-size: 56px !important;
                  line-height: 72px !important;
                  margin-bottom: 1rem !important;
                  margin-top: 1.5rem !important;
                  color: ${theme?.colors?.textDark || "#000000"} !important;
                }
                .contact-section-content h2 {
                  font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                  font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                  font-size: 40px !important;
                  line-height: 48px !important;
                  margin-bottom: 1rem !important;
                  margin-top: 1.5rem !important;
                  color: ${theme?.colors?.textDark || "#000000"} !important;
                }
                .contact-section-content h3 {
                  font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                  font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                  font-size: 27px !important;
                  line-height: 40px !important;
                  margin-bottom: 1rem !important;
                  margin-top: 1.5rem !important;
                  color: ${theme?.colors?.textDark || "#000000"} !important;
                }
                .contact-section-content h4 {
                  font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                  font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                  font-size: 20px !important;
                  line-height: 32px !important;
                  margin-bottom: 0.75rem !important;
                  margin-top: 1.25rem !important;
                  color: ${theme?.colors?.textDark || "#000000"} !important;
                }
                .contact-section-content h5 {
                  font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                  font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                  font-size: 18px !important;
                  line-height: 28px !important;
                  margin-bottom: 0.5rem !important;
                  margin-top: 1rem !important;
                  color: ${theme?.colors?.textDark || "#000000"} !important;
                }
                .contact-section-content h6 {
                  font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                  font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                  font-size: 16px !important;
                  line-height: 24px !important;
                  margin-bottom: 0.5rem !important;
                  margin-top: 1rem !important;
                  color: ${theme?.colors?.textDark || "#000000"} !important;
                }
                @media (max-width: 768px) {
                  .contact-section-content h1 {
                    font-size: 32px !important;
                    line-height: 48px !important;
                  }
                  .contact-section-content h2 {
                    font-size: 27px !important;
                    line-height: 32px !important;
                  }
                  .contact-section-content h3 {
                    font-size: 22px !important;
                    line-height: 32px !important;
                  }
                  .contact-section-content h4 {
                    font-size: 18px !important;
                    line-height: 24px !important;
                  }
                  .contact-section-content h5 {
                    font-size: 16px !important;
                    line-height: 24px !important;
                  }
                  .contact-section-content h6 {
                    font-size: 14px !important;
                    line-height: 20px !important;
                  }
                }
              `}</style>
              <div
                className="contact-section-content prose prose-lg mx-auto mb-8 max-w-none text-center text-[20px]" // eslint-disable-line tailwindcss/no-custom-classname
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  color: theme?.colors?.textDark || "#000000"
                }}
                dangerouslySetInnerHTML={{
                  __html: section.body.replace(/Phone:\s*[\d\-+()\s]+/gi, "").replace(/928-277-5778/g, "")
                }}
              />
            </>
          )}
        </div>

        {/* Contact Form */}
        {showContactForm && <div className="mx-auto mb-12 max-w-3xl">{renderContactForm()}</div>}

        {/* Contact Info (Email & Phone) */}
        {contactInfo && <div className="mb-8">{contactInfo}</div>}

        {/* Fallback - Force show contact info if not showing */}
        {!contactInfo && ((showEmail && !!email) || (showPhoneNumber && !!phoneNumber)) && (
          <div className="mb-8 space-y-2 text-center">
            {showEmail && email && (
              <p
                className="text-lg"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  color: theme?.colors?.textDark || "#000000"
                }}>
                {email}
              </p>
            )}
            {showPhoneNumber && phoneNumber && (
              <p
                className="text-lg"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "sans-serif",
                  color: theme?.colors?.textDark || "#000000"
                }}>
                {formatPhoneNumber(phoneNumber)}
              </p>
            )}
          </div>
        )}

        {/* Social Links */}
        {socialLinks && <div className="mb-12">{socialLinks}</div>}

        {/* Divider */}
        {showMap && (socialLinks || contactInfo) && (
          <div className="mb-12">
            <div className="h-px w-full" style={{ backgroundColor: theme?.colors?.stroke || "#e6e6e6" }} />
          </div>
        )}

        {/* Map */}
        {showMap &&
          (() => {
            // Filter for physical locations - handle locations without kind or with empty kind
            const physicalLocations =
              practice?.locations?.filter((location) => {
                // If no kind is specified, assume it's a physical location if it has coordinates
                if (!location.kind || location.kind === "") {
                  return location.latitude && location.longitude
                }
                // Otherwise check for known physical location kinds
                return location.kind === "physical" || location.kind === "office" || location.kind === "home"
              }) || []

            return (
              <div>
                {physicalLocations.length > 0 ? (
                  <div className={twMerge("overflow-hidden", getThemeShapeClasses(theme))} style={{ height: "300px" }}>
                    <Map
                      practice={practice}
                      theme={{
                        colorAccent: theme?.colors?.accent || "#0891b2",
                        colorTextLight: "#ffffff",
                        colorBgLight: theme?.colors?.background || "#ffffff"
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className={twMerge(
                      "flex h-64 items-center justify-center border bg-gray-50",
                      getThemeShapeClasses(theme)
                    )}
                    style={{ borderColor: theme?.colors?.stroke || "#e6e6e6" }}>
                    <div className="text-center">
                      <p className="mb-2" style={{ color: theme?.colors?.textDark || "#000000" }}>
                        No office location available
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
      </AnimatedSection>
    </section>
  )
}

export default ContactSection
